// Which browser on which device, for installing (INSTALL.md §2).
//
// THE GUIDANCE BRANCHES ON THE BROWSER, NOT ONLY THE OS (§2): Chrome on an
// iPhone installs through its own Share button, Chrome on Android through a
// prompt we can raise, Firefox on a computer not at all. So this names the
// pair, and `steps.ts` has one set of steps for each.
//
// A user-agent string is a guess a browser makes about itself, and that is
// fine here: a wrong guess shows the wrong steps above a list of every other
// platform's (`InstallScreen`), never a dead button.

export type InstallPlatform =
  | 'ios-safari'
  | 'ios-other'
  | 'android-chrome'
  | 'android-other'
  | 'desktop-chrome'
  | 'desktop-edge'
  | 'mac-safari'
  | 'desktop-firefox'
  | 'other';

/** iPadOS reports itself as a Mac; a Mac with a touch screen is an iPad. */
export function isAppleTouch(ua: string, maxTouchPoints: number): boolean {
  return /iPhone|iPad|iPod/.test(ua) || (/Macintosh/.test(ua) && maxTouchPoints > 1);
}

export function platformOf(ua: string, maxTouchPoints: number): InstallPlatform {
  if (isAppleTouch(ua, maxTouchPoints)) {
    return /CriOS|EdgiOS|FxiOS|OPiOS/.test(ua) ? 'ios-other' : 'ios-safari';
  }
  if (/Android/.test(ua)) {
    if (/SamsungBrowser|Firefox|OPR|EdgA|UCBrowser|YaBrowser|MiuiBrowser/.test(ua)) {
      return 'android-other';
    }
    return /Chrome\//.test(ua) ? 'android-chrome' : 'android-other';
  }
  if (/Edg\//.test(ua)) return 'desktop-edge';
  if (/Firefox\//.test(ua)) return 'desktop-firefox';
  if (/Chrome\/|Chromium\//.test(ua)) return 'desktop-chrome';
  if (/Macintosh/.test(ua) && /Safari\//.test(ua)) return 'mac-safari';
  return 'other';
}

/** This browser, as `platformOf` reads it. */
export function thisPlatform(): InstallPlatform {
  if (typeof navigator === 'undefined') return 'other';
  return platformOf(navigator.userAgent, navigator.maxTouchPoints ?? 0);
}

/**
 * Am I in the installed app right now (INSTALL.md §4.4)? `display-mode`
 * everywhere, `navigator.standalone` on iOS, and both, because the second is
 * being deprecated. This does not say whether an install exists somewhere
 * else — the account does (`GET /api/me/install`).
 */
export function isInstalled(): boolean {
  if (typeof window === 'undefined') return false;
  const standalone = (navigator as Navigator & { standalone?: boolean }).standalone === true;
  return standalone || window.matchMedia?.('(display-mode: standalone)').matches === true;
}

/** A phone or a tablet, where the app goes on a home screen rather than in a window. */
export function isHandheld(platform: InstallPlatform): boolean {
  return platform.startsWith('ios-') || platform.startsWith('android-');
}
